import React, { useState } from 'react';

import { useNavigate } from 'react-router-dom';

import Tooltip from '@mui/material/Tooltip';
import NotOfferIcon from 'assets/LeadInfo/NotOfferIcon';
import PersonImg from 'assets/LeadInfo/person.svg';
import BuildingImg from 'assets/myLeads/company.svg';
import MapIcon from 'assets/myLeads/map.svg';
import PhoneIcon from 'assets/myLeads/phone.svg';
import WarningIcon from 'assets/myLeads/warning.svg';

import PriorityDropdown from '../PriorityDropdown';
import {
  BuildingIcon,
  CardContainer,
  CardContent,
  CardTop,
  CheckBoxBlock,
  CompanyIcon,
  CompanyName,
  CompanyText,
  HoverText,
  InfoIcon,
  InfoImg,
  InfoRow,
  InfoText,
  RightCheckbox,
  WarningBadge,
  WarningImg,
  WarningWrapper,
} from './LeadCard.styles';

const LeadCard = ({
  lead,
  isDragging,
  isSelected,
  onSelect,
  onPriorityChange,
  statusColor,
  canEditPriority,
}) => {
  const navigate = useNavigate();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isPressed, setIsPressed] = useState(false);

  const handleCheckboxChange = (e) => {
    e.stopPropagation();
    onSelect(lead.id);
  };

  const handleCardClick = () => {
    if (!isDragging && !isDropdownOpen) {
      navigate(`/lead-info?id=${lead.id}`);
    }
  };

  const hasNoOffer = !lead.offers?.length;

  // Build full address from all available parts
  const formatFullAddress = () => {
    const addressParts = [];
    if (lead.country?.name) addressParts.push(lead.country.name);
    if (lead.region?.name) addressParts.push(lead.region.name);
    if (lead.district?.name) addressParts.push(lead.district.name);
    if (lead.city?.name) addressParts.push(lead.city.name);
    if (lead.street?.name) addressParts.push(lead.street.name);
    if (lead.buildingHouse?.name) addressParts.push(lead.buildingHouse.name);
    if (lead.address) addressParts.push(lead.address);

    return addressParts.join(', ') || '-';
  };

  const fullAddress = formatFullAddress();

  return (
    <CardContainer
      $isDragging={isDragging}
      $isSelected={isSelected}
      $isPressed={isPressed}
      $statusColor={statusColor}
      $isDropdownOpen={isDropdownOpen}
      $isWarning={lead.isWarning}
      onMouseDown={() => setIsPressed(true)}
      onMouseUp={() => setIsPressed(false)}
      onMouseLeave={() => setIsPressed(false)}
      onClick={handleCardClick}
    >
      {lead.isWarning && (
        <WarningBadge $type="warning">
          <WarningImg src={WarningIcon} />
        </WarningBadge>
      )}

      {hasNoOffer && (
        <WarningBadge $type="no-offer" $offset={lead.isWarning ? 28 : 0}>
          <WarningWrapper>
            <NotOfferIcon />
            <HoverText>No offer assigned</HoverText>
          </WarningWrapper>
        </WarningBadge>
      )}
      <CardContent>
        <CardTop>
          <CompanyIcon>
            <BuildingIcon
              $src={lead.businessType === 'B2B' ? BuildingImg : PersonImg}
              $statusColor={statusColor}
              $isWarning={lead.isWarning}
            />
            <CompanyText>{lead.businessType === 'B2B' ? 'Company' : 'Contact'}</CompanyText>
          </CompanyIcon>

          <PriorityDropdown
            onOpenChange={(open) => setIsDropdownOpen(open)}
            currentPriority={lead.priority}
            onPriorityChange={(newPriority) => onPriorityChange(lead.id, newPriority)}
            canEditPriority={canEditPriority}
          />
        </CardTop>

        <CompanyName>{lead.companyName}</CompanyName>

        <InfoRow>
          <InfoIcon>
            <InfoImg src={PhoneIcon} />
          </InfoIcon>
          <InfoText>{lead.phoneNumber}</InfoText>
        </InfoRow>

        <InfoRow>
          <InfoIcon>
            <InfoImg src={MapIcon} />
          </InfoIcon>
          {fullAddress.length > 7 ? (
            <Tooltip title={fullAddress} placement="top" arrow>
              <InfoText>{fullAddress}</InfoText>
            </Tooltip>
          ) : (
            <InfoText>{fullAddress}</InfoText>
          )}
        </InfoRow>

        {lead.assignee && lead.assignee.name && (
          <InfoRow>
            <InfoText>
              Assigned to {''}
              <span className="assign-to">
                {lead.assignee?.name} {lead.assignee?.surname || ''}
              </span>
            </InfoText>
          </InfoRow>
        )}

        <InfoRow>
          <CheckBoxBlock
            onClick={(e) => {
              e.stopPropagation();
              onSelect(lead.id);
            }}
          >
            <RightCheckbox
              type="checkbox"
              checked={isSelected}
              onChange={handleCheckboxChange}
              readOnly
            />
          </CheckBoxBlock>
        </InfoRow>
      </CardContent>
    </CardContainer>
  );
};

export default LeadCard;
